import { DatePipe } from '@angular/common';

/**
*@function parseDateTime Will return the values of the date in the next format
*@example '08/13/2022 00:00'
*@param date Will recive a date in the next format
*@example '2018-12-31'
**/
export function provisionParseDateTime(dateInput: string): string {
  const date = dateInput.split('T')[0].split('-');
  const time = dateInput.split('T')[1].split(':');
  const year = date[0];
  const month = date[1];
  const day = date[2];
  const hour = time[0];
  const minutes = time[1];
  const seconds = time[2].split('.')[0];
  return `${month}/${day}/${year} ${hour}:${minutes}:${seconds}`;
}

/**
*@function parseDateTime Will return the values of the date in the next format
*@example '2020-10-06T21:02:31.426+0000'
*@param date Will receive a date in the next format
*@example 'Tue Oct 06 2020 16:02:31 GMT-0500 (Central Daylight Time)'
**/
export function parseDateTime(dateInput: string): string {
  const createdDateTime = new Date(Date.parse(provisionParseDateTime(dateInput)));
  return createdDateTime.toString();
}

/**
* transformDateTime function will convert given DateTime to system local DateTime
* ex: input - any datetime format
* ex: output - Wed, Jun 7, 2023, 4:58:52 PM
**/
export function transformDateTime(dateInput: any) {
  const datePipe = new DatePipe('en-US');
  return datePipe.transform(dateInput, 'EEE, MMM d, y, h:mm:ss a');
}

/**
* transformDateTime function will convert given DateTime to system local DateTime
* ex: input - any datetime format
* ex: output - Wed, Jun 7, 2023, 4:58:52 PM
**/
export function transformDateTimeLogin(dateInput: any) {
  const datePipe = new DatePipe('en-US');
  return !isNaN(dateInput) ? datePipe.transform(dateInput, 'EEE, MMM d, y, h:mm:ss a') : ""
}

/**
* @function getFormattedDate: Gets the formatted value of the date
*@example 'gets the date which is 2021-04-20 format'
*@param date Will receive a date in the next format
*@example 'OCT 06, 2020'
**/
export function getFormattedDate(input): string {
  if (input && input.split('-').length > 1) {
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const year = input.split('-')[0];
    const month = input.split('-')[1];
    const day = input.split('-')[2];
    const monthName = month[0] === '0' ? months[month[1] - 1] : months[month - 1];
    return `${monthName} ${day},${year}`;
  }
  return input;
}


/**
 * @function getSubproperty function to show nested properties
 */
export function getSubproperty(pathList, obj): string {
  const propertyMap = pathList.map(path => {
    const properties = Array.isArray(path) ? path : path.split('.');
    const propertyReducer = properties.reduce((prev, curr) => prev && prev[curr], obj);
    return propertyReducer;
  });
  return propertyMap.filter(property => property)[0];
}

/**
* @function formatDateString: Gets the formatted value of the date
*@example 'gets the dateString in 03/12/2019 6:02 AM CST'
*@param date Will receive a date in the next format
*@example 'MAR 12,2019 6:02 AM CST'
**/

export function formatDateString(dateString): string {
  if (dateString.split('/').length > 1) {
    const temp = dateString.split(' ')[0];
    const timeString = dateString.split(' ').splice(1, 3);
    const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];
    const year = temp.split('/')[2];
    const month = temp.split('/')[1];
    const day = temp.split('/')[0];
    const monthName = month[0] === '0' ? months[month[1] - 1] : months[month[0]];
    return `${monthName} ${day},${year} ${timeString[0]} ${timeString[1]} ${timeString[2]}`;
  }
  return dateString;
}

/**
   * formatDate helper function to format the date for the given format_order and separater
**/
export function formatDate(date: Date, format_order = ['yyyy', 'mm', 'dd'], seperator = '-'): string {
  const format = {
    'yyyy': date.getFullYear(),
    'mm': date.getMonth() < 9 ? '0' + (date.getMonth() + 1) : date.getMonth() + 1,
    'dd': date.getDate() < 10 ? '0' + date.getDate() : date.getDate()
  };
  return format_order.map((x) => format[x].toString()).join(seperator);
}

/**
* convertToYearsAndMonths converts given months to number of years and months
* ex: input - 12 MTH returns to 1 Year
* ex: input - 45 MTH returns to 3 Years and 3 Months
**/
export function convertToYearsAndMonths(term): string {
  const years = Math.floor(term / 12);
  const months = term % 12;
  if (years === 1) {
    return years + ' Year';
  } else if (years === 0) {
    return months + ' Months';
  } else if (years > 1 && months > 1) {
    return years + ' Years ' + months + ' Months';
  } else {
    return years + ' Years';
  }
}

/*
* generateRandomNumber method will generate a random 'lengthOfRandomNumber' digit number
* input 2 - random number 66
* input 5 - random number 76766
**/
export function generateRandomNumber(lengthOfRandomNumber: number) {
  let add = 1, max = 12 - add;
  if (lengthOfRandomNumber > max) {
    return generateRandomNumber(max) + generateRandomNumber(lengthOfRandomNumber - max);
  }
  max = Math.pow(10, lengthOfRandomNumber + add);
  const min = max / 10;
  const number = Math.floor(Math.random() * (max - min + 1)) + min;
  return ('' + number).substring(add);
}


/*
* convertEpochToGMT method will convert Epoch Milliseconds to GMT Format
* input  - Epoch Milliseconds eg: 1632511625
* output - Fri Sep 24 2021 14:27:05 GMT-0500 (Central Daylight Time)
**/
export function convertEpochToGMT(input: number) {
  const seconds = input * 1000;
  return new Date(seconds);
}
/*
* parseIsoDate method will convert Iso basic timestamp to local time Format
* input  - Iso Basic Timestamp  eg: 20251120123212Z
* output - Thu Nov 20 2025 12:32:12 PM (Local Time)
**/
export function parseIsoDate(dateInput: string): Date {
  const IsoRegexCheck = /^\d{14}Z$/
  const epochRegexCheck = /^\d{10}$/

  if (IsoRegexCheck.test(dateInput)) {
    const year = +dateInput.slice(0, 4);
    const month = +dateInput.slice(4, 6) - 1;
    const day = +dateInput.slice(6, 8);
    const hour = +dateInput.slice(8, 10);
    const minutes = +dateInput.slice(10, 12);
    const seconds = +dateInput.slice(12, 14);
    return new Date(Date.UTC(year, month, day, hour, minutes, seconds));
  }

  if (epochRegexCheck.test(dateInput)) {
    return convertEpochToGMT(+dateInput)
  }
}
/*
* convertDateTime formats a Date to CST and AM/PM
* input - Fri Sep 24 2021 14:27:05 GMT-0500 (Central Daylight Time)
* output - Fri Sep 24 2021 02:27:05 PM CST
*/
export function convertDateTime(input: any) {
  let tempString = input.toString();
  let tempHourStrToInt = parseInt(tempString.split(' ')[4].substring(0, 2));
  let tempHourIntToStr = '';
  let a = 'AM';
  if (tempHourStrToInt > 12) {
    a = 'PM';
    tempHourStrToInt = ((tempHourStrToInt + 11) % 12 + 1);
  }
  if (tempHourStrToInt < 10) {
    tempHourIntToStr = '0' + tempHourStrToInt.toString();
  } else {
    tempHourIntToStr = tempHourStrToInt.toString();
  }
  tempString = tempString.substring(0, 16) + '- ' + tempHourIntToStr + tempString.substring(18, 25) + a + ' CST';
  return tempString;
}

/**
  * @function phone_format Format value of phoneNumber
  * @returns string format ex. '(yyyy) yyy-yyyy'
  **/
export function phone_format(value: string): string {
  return `${value.toString().slice(0, 1)}` !== '(' ? `(${value.toString().slice(0, 3)}) ${value.toString().slice(3, 6)}-${value.toString().slice(6)}` :
    !!this.customer ? this.customer['phoneNumbers'][0]['phoneNumber'] : '';
}
/**
*@function validExts Will return the list of valid extensions for a csv files
*@example '.csv'
**/
export function validExts(file): boolean {
  const extsList = new Array('.csv');
  let fileExt = file.file.name;
  fileExt = fileExt.substring(fileExt.lastIndexOf('.'));
  return extsList.indexOf(fileExt) < 0;
}

/**
* @var isEmpty returns true if the object is empty
**/
const isEmpty = inputObject => {
  return Object.keys(inputObject).length === 0;
};

/**
* @function csvJSON converts the csv file to jason object
**/
export function csvJSON(csv: any): object {
  const lines = csv.split('\n');
  const result = [];
  const headers = lines[0].split('\r');
  for (let i = 1; i < lines.length; i++) {
    const obj = {};
    const currentline = lines[i].split('\r');
    for (let j = 0; j < headers.length; j++) {
      if (!!currentline[j]) {
        obj[headers[j]] = currentline[j];
      }
    }
    if (!isEmpty(obj)) {
      result.push(obj);
    }
  }
  return result;
}

/*
* convertUTCToMMDDYYYY method will convert UTC time to MM/DD/YYYY
**/
export function convertUTCToMMDDYYYY(input: string) {
  if (!!input) {
    let output = input.split('T')[0].split('-');
    return output[1] + '/' + output[2] + '/' + output[0]
  }
}

/*
* convertUTCToAMPM method will convert UTC time to AM or PM
**/
export function convertUTCToAMPM(input: string) {
  if (!!input) {
    let output = new Date(input).toString().split(' ')[4]
    if (parseInt(output.split(':')[0]) > 12) {
      const hr = 24 - parseInt(output.split(':')[0]);
      return hr.toString() + ':' + output.split(':')[1] + ' PM';
    } else {
      return parseInt(output.split(':')[0]).toString() + ':' + output.split(':')[1] + ' AM';
    }
  }
}
